import { FLAGS } from 'constants/flags';
import type { TrackFunction } from 'modules/analytics/lib/tracking';
import { useCurrentUser } from 'modules/auth/AuthContext';
import { useCallback } from 'react';
import { isHibooUser } from './isHibooUser';
import { useFeature } from './use-feature';

type FaultEvents =
  | 'See original description'
  | 'See fault report'
  | 'See fault asset profile'
  | 'View switched';
type AlertEvents =
  | 'Alert created'
  | 'Alert updated'
  | 'Alert deleted'
  | 'Alert canceled';
type DestinationsPageEvents =
  | 'API key created'
  | 'API key removed'
  | 'API key rotated';
type DataKindEvents = 'Datatype selected';
type MapEvents =
  | 'Map address searched'
  | 'Map asset clicked'
  | 'Map asset list to asset profile navigation'
  | 'Map asset from list clicked'
  | 'Map zone list to zone page navigation'
  | 'Map zone from list clicked'
  | 'Map asset by name searched'
  | 'Map asset list filtered'
  | 'Map zone by name searched'
  | 'Map zone list filtered'
  | 'Map Geozones option changed'
  | 'Map Use category color option changed'
  | 'Map Use custom field color option changed'
  | 'Map Cluster option changed'
  | 'Map Use custom imagery option changed'
  | 'Map Tooltip option changed'
  | 'Map Marker color option changed';
type MaintenanceEvents =
  | 'maintenance_plan_created'
  | 'maintenance_plan_deleted'
  | 'issue_created'
  | 'issue_status_changed'
  | 'issue_assignee_changed'
  | 'issue_severity_changed'
  | 'issue_comment_added'
  | 'issue_file_uploaded'
  | 'out_of_contract_usage_comment_added'
  | 'work_order_created'
  | 'work_order_created_from_document'
  | 'work_order_import_document_clicked'
  | 'work_order_status_changed'
  | 'work_order_assignee_changed'
  | 'work_order_severity_changed'
  | 'work_order_comment_added'
  | 'work_order_file_uploaded';

type OutOfContractEvents =
  | 'ooc_rules_configured'
  | 'ooc_page_viewed'
  | 'ooc_status_changed'
  | 'ooc_billing_amount_edited'
  | 'ooc_export_triggered'
  | 'ooc_report_subscription_set'
  | 'ooc_billing_potential_amount_edited';

type FieldEvents =
  | 'Legacy QR Code Assign Clicked'
  | 'QR Code Asset Ignored'
  | 'QR Code Asset Unignored'
  | 'QR Code Public Page Link Copied'
  | 'QR Code Downloaded'
  | 'QR Code Public Page Viewed';

export type EventName =
  | 'Filter selected'
  | 'Period selected'
  | 'Report exported'
  | 'Switch report clicked'
  | 'Paywall CTA clicked'
  | 'Periodicity selected'
  | 'Report asset list lookup opened'
  | 'Segment created'
  | 'Segment applied'
  | 'Shortcut applied'
  | 'Assetdocument uploaded'
  | 'Assetdocument downloaded'
  | 'Asset archived'
  | 'Asset unarchived'
  | 'Asset deleted'
  | 'Sharing created'
  | 'Sharing edited'
  | 'Sharing stopped'
  | 'AIcategorysuggestion displayed'
  | 'AIcategorysuggestion selected'
  | 'Create sharing clicked from asset menu'
  | 'Create sharing clicked from asset profile'
  | 'Public Url Copied'
  | 'QR Code Assigned'
  | 'Nearby Assets Clicked'
  | 'Nearby Assets Itinerary Viewed'
  | 'Nearby Assets Searched'
  | 'Number of result changed'
  | 'Column updated'
  | 'Column sorted'
  | 'Tyre visibility toggle'
  | 'Zone archived'
  | 'Zone unarchived'
  | MapEvents
  | DestinationsPageEvents
  | FaultEvents
  | AlertEvents
  | DataKindEvents
  | MaintenanceEvents
  | OutOfContractEvents
  | FieldEvents;

export function buildContext(
  user:
    | {
        email?: string | null;
        workspaceId?: number | null;
      }
    | undefined,
  includeHiboo = false,
) {
  if (!user) return {};
  const { email, workspaceId } = user;
  if (!email || !workspaceId || (!includeHiboo && isHibooUser(email)))
    return {};

  return {
    workspaceId,
  };
}

export type TrackingProps = {
  page?: string;
  section?: string;
  [key: string]: unknown;
};

export function useTracking<
  E extends string = EventName,
  P extends object = TrackingProps,
>() {
  const { user } = useCurrentUser();
  const [trackHibooUser] = useFeature(FLAGS.MIXPANEL_HIBOO);

  const track = useCallback<TrackFunction<E, P>>(
    (eventName: E, properties: P) => {
      if (user && isHibooUser(user.email || '') && !trackHibooUser) {
        return;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.debug('track', eventName, properties);
      }
      analytics.track(eventName, properties, {
        context: buildContext(user, trackHibooUser),
      });
    },
    [user, trackHibooUser],
  );

  return { track };
}
